import React, { Suspense, useEffect, useLayoutEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useCookies } from 'react-cookie';

import { UserContext, useUserState, authAPI } from '@mirinae/react-auth';
import { UrlParamContext, useUrlParamContext } from '@mirinae/shared/contexts/urlParams';
import { KeyboardContext, useKeyboardContext } from '@mirinae/shared/contexts/keyboard';
import { FeatureFlagContext, useFeatureFlags } from '@mirinae/shared/contexts/featureFlags';

import { UserAgent } from '@mirinae/js-utils';
import { classes } from '@mirinae/react-ui';
import { FB_PIXELID } from '@mirinae/defines/keys';
import { apiPaths, appRoots, wikinaePaths, explorerPaths, lessonsPaths, membersPaths, pagePaths, universityEntryPath, universityGuestDestination } from '@mirinae/defines/paths';
import { useAppVersionStoreMethods, useAppVersionStore } from "@mirinae/stores/appVersion";
import { httpAPI } from '@explorer/modules/apis/http';
import GTM from '@mirinae/apis/GoogleAnalytics';
import '@mirinae/apis/activity';   // adds the platform header to our own API calls
import GlobalStyling from '@mirinae/defines/GlobalStyling';
import LoadingSpinner from '@mirinae/react-ui/components/spinner/LoadingSpinner';
import LoginAsBanner from '@mirinae/shared/components/LoginAsBanner';

// note that we can even do dynamic loads on individual user-events:
// button.addEventListener('click', event => {
//   import('./someModule').then(module => {
//     module.default();
//   });
// });

// Which app a URL belongs to, from its first path segment -- the same roots the Nginx/devServer
// rewrites and the front-end server's catch-all use to pick each app's index.html. Unknown roots
// (including '/') fall through to explorer, matching that catch-all.
const appRootNames = Object.values(appRoots);
const appFromPath = () => {
    const root = window.location.pathname.split('/')[1];
    return appRootNames.includes(root) ? root : appRoots.explorer;
};

// GA4 user ID, primary path: the auth API reports every user change synchronously, before it
// updates the user context, so the ID is set before React renders anything that sends analytics --
// including components that send from layout effects, which run before this component's own
// (ExplorationContainer, EmbeddedExplorationContainer, lessons Player). See GTM.setUser.
authAPI.onUserChange(GTM.setUser);

// Interim sign-up policy (John, 2026-09-17): on the web, guests may only open these pages; any other
// URL sends them to the explorer landing page. See docs/MIRINAE_FEEDBACK.md, "Access to the analyzer
// without signing up". Everything under /auth stays open so sign-up, login, reset and email
// verification never loop.
const guestAllowedPaths = [explorerPaths.landing, pagePaths.landing, lessonsPaths.home, lessonsPaths.landing, membersPaths.plan,
    // the address printed on coupons (#116): it sends a signed-out visitor to sign in and back, not to the landing page
    membersPaths.redeem,
    // public company, legal and help pages: linked from sign-up consent, emails and the app's purchase screen, and must
    //   open for people who have not signed up
    explorerPaths.about, explorerPaths.termsAndConditions, explorerPaths.privacyPolicy, explorerPaths.eula,
    explorerPaths.communityGuidelines, explorerPaths.help];
// `universityEntryPath` is a school's own login and sign-up: the list above is matched exactly and
// those carry an alias, so they need a pattern (mirinae-mono#26). Without it the rule locks the door
// it is standing in front of -- an invited student opening /university/<alias>/signup is sent to the
// landing page and can never begin, and a school administrator cannot sign in from their school's
// address either. Only those pages open, and the invitation pages (a school's, and a distributor's, #77): every other university page still asks for a session,
// and the analyzer stays behind sign-up, which is what the policy above is for.
// The K-Beauty mock's pages (docs/content-marketing/content-sections-design.md section 10.3) are open to guests,
// as John decided for guide pages (2026-11-04), but only on test sites and locally while they hold mock content.
// The whole /kbeauty route family opens, not only its root: the list above is matched exactly.
const onTestSite = window.location.host.match(/^(alpha|seoul)\./) !== null || process.env.REACT_APP_MODE === 'LOCAL';
const isGuestContentPath = (path) => onTestSite && (path === `/${appRoots.kbeauty}` || path.startsWith(`/${appRoots.kbeauty}/`));

const isGuestAllowedPath = (pathname) => {
    const path = pathname.replace(/\/+$/, '') || '/';
    return path === `/${appRoots.auth}` || path.startsWith(`/${appRoots.auth}/`)
        || guestAllowedPaths.includes(path) || universityEntryPath.test(path) || isGuestContentPath(path);
};

// Local sign-ups stay role 'guest' until their email is verified; they are not sent back to landing.
const isUnregisteredGuest = (user) => user?.role === 'guest' && user.status !== 'awaiting-verification';

// Version-dependent flags, derived in one place so the first-pass determination and the late
// correction below cannot drift apart. Pure: no store writes, no ad creation, no path rewrites.
export const deriveVersionFlags = (isApp, versionParam = '0') => {
    if (!isApp) {
        // web or mobile-web is always pro-enabled
        return { appVersion: 0, isProEnabledVersion: true, isPricePlan3PlusEnabled: true, isAdMobAdsEnabled: false };
    }
    const appVersion = /^[0-9.]+\/*$/.test(versionParam)
        ? versionParam.split('.').reduce((a, v, i) => a + (Number.parseInt(v, 10) || 0) / 10 ** i, 0)
        : 0;
    return {
        appVersion,
        isProEnabledVersion: appVersion >= 1.54,
        isPricePlan3PlusEnabled: appVersion >= 2.16,
        // AdMob ads are off for now: they earn almost nothing and the reward-unlock flow is
        // hard to keep correct. Re-enable by restoring: isAdMobAdsEnabled: appVersion >= 2.2
        isAdMobAdsEnabled: false,
    };
};

const Base = (props) => {
    const userState = useUserState();
    const [userContext, setUserContext] = userState;
    const keyboardState = useKeyboardContext();
    const featureFlags = useFeatureFlags();
    const urlParams = useUrlParamContext(featureFlags);
    const cookieState = useCookies([]);
    const [testSite, setTestSite] = useState(false);
    const _isAppChecked = useRef(false);
    const { setVersionData } = useAppVersionStoreMethods();
    const { os } = UserAgent;
    const location = useLocation();

    let isApp = useAppVersionStore(state => state.isApp);
    const isAppChecked = useAppVersionStore(state => state.isAppChecked);

    // Web guests go to the landing page from any page not in the allowed list. Until the app check
    // settles (async in the Flutter webview) we hold rendering rather than flash the page.
    const guestOnBlockedPage = isUnregisteredGuest(userContext.user) && !isGuestAllowedPath(location.pathname);
    const sendGuestToLanding = guestOnBlockedPage && isAppChecked && !isApp;
    const holdForLanding = guestOnBlockedPage && (!isAppChecked || sendGuestToLanding);

    // Automatic page_view on every route change, for all six apps. Routes that send their own
    // richer pageview suppress this one (see GTM.autoPageView). Previously every pageview had to
    // be hand-written in a component, so any route without one was invisible -- 10 of the 11
    // lessons pages, for instance. See docs/analytics-tagging-audit.md.
    // Waits until the user is known. Nothing below renders before then, so a route cannot send its
    // own pageview earlier anyway; scheduling the fallback sooner only let a slow session check fire
    // it first, without the user id and ahead of the route's own, richer pageview.
    useEffect(() => {
        if (!userContext.user?.role || holdForLanding) return;
        GTM.autoPageView(appFromPath());
    }, [location.pathname, userContext.user?.role, holdForLanding]);

    // GA4 user ID backstop. The primary path is authAPI.onUserChange (registered above), which runs
    // before the context update; this catches any user change made some other way. Keyed on the ID
    // setUser would send, so every eligibility input -- id, role, loginType -- is covered; setUser
    // dedupes, so the two paths never double-send.
    const gaUserId = GTM.eligibleUserId(userContext.user);
    useLayoutEffect(() => {
        GTM.setUser(userContext.user);
    }, [gaUserId]);

    if (!_isAppChecked.current) {
        // check if app, derive version as float & check various version-dependent enablements
        _isAppChecked.current = true;

        const setAppParameters = (versionParam = '0') => {
            // app status determined, version determined, derive various flags & store.
            // Derived through deriveVersionFlags so this path and the late-correction effect
            // below cannot drift apart on the version thresholds.
            const { appVersion, isProEnabledVersion, isPricePlan3PlusEnabled, isAdMobAdsEnabled } = deriveVersionFlags(isApp, versionParam);

            if (isApp) {
                window.versionParam = versionParam;

                if (appVersion <= 2.15) {
                    // hack for <= v2.1.5 mobile apps, need to see a '.' following 'wikinae' to condition wikinae screens correctly
                    appRoots.wikinae = 'wikinae.';
                    for (const path in wikinaePaths) {
                        wikinaePaths[path] = wikinaePaths[path].replace('wikinae', 'wikinae.').replace(/\.$/, './');
                    }
                }

                if (isAdMobAdsEnabled && window.flutter_inappwebview) {
                    // create AdMob ad units ahead of time
                    httpAPI('', apiPaths.listActiveAdUnits, { method: 'GET' })
                        .then(response => {
                            const { adUnits = [] } = response.data;
                            for (const adUnit of adUnits) {
                                for (const type in adUnit) {
                                    console.log('call CreateAd', type, adUnit[type]);
                                    window.flutter_inappwebview.callHandler('CreateAd', { type, unitId: adUnit[type] });
                                }
                            }
                        });
                }
            }

            setTestSite(window.location.host.match(/^(alpha|seoul|local|mirinae\.dev)/));

            console.log('isApp', isApp, 'appVersion', appVersion, 'isProEnabledVersion', isProEnabledVersion, 'isPricePlan3PlusEnabled', isPricePlan3PlusEnabled, 'isAdMobAdsEnabled', isAdMobAdsEnabled);
            setVersionData({ isAppChecked: true, isApp, versionParam, appVersion, isProEnabledVersion, isPricePlan3PlusEnabled, isAdMobAdsEnabled, displayMustUpgradeApp: null });
       }

        const fallbackCheck = () => {
            // older app, no GetAppVersion support, check for window.appVersion & URL v= query params
            console.log('fallbackCheck');
            const versionParam = new URLSearchParams(window.location.search).get('v')?.replace('/', '') || window.appVersion;
            isApp = (os.isAndroid || os.isIos) && (Boolean(versionParam) || window.inAppView);
            setAppParameters(versionParam);
        }

        // latest apps will tell us version, should have been in apps from the start!!
        if (window.flutter_inappwebview) {
            console.log('window.flutter_inappwebview true');
            isApp = true;
            window.flutter_inappwebview.callHandler('GetAppVersion', { })
                .then(versionParam => setAppParameters(versionParam))
                .catch(() => fallbackCheck());
        } else {
            // not in an app, fill in default values, complete other checks
            fallbackCheck();
        }
    }

    // Late app detection. The block above decides once and never revisits, but it can only see
    // window.flutter_inappwebview if the bridge already exists, and each app's index.js waits just
    // 250ms twice for it. On a slow cold start we therefore conclude "web" while actually running
    // inside the app, permanently: store banners, web-only prompts and the landing redirect all
    // follow. Positive detection is never revoked here, only a provisional negative is corrected.
    //
    // This is mitigation, not a guarantee: a launch slower than every signal we wait for still
    // loses. The durable fix is a marker injected natively before page scripts run, which needs
    // an app release.
    useEffect(() => {
        if (isApp || !(os.isAndroid || os.isIos)) return undefined;

        let done = false;
        const promote = (versionParam) => {
            if (done) return;
            done = true;
            // Flags only. The one-time work in setAppParameters (the wikinae path rewrite, ad-unit
            // creation) must not be replayed, and displayMustUpgradeApp must not be reset.
            setVersionData({ isAppChecked: true, isApp: true, versionParam, ...deriveVersionFlags(true, versionParam) });
        };

        const onReady = () => {
            if (window.flutter_inappwebview?.callHandler) {
                window.flutter_inappwebview.callHandler('GetAppVersion', {})
                    .then(v => promote(String(v ?? '0')))
                    .catch(() => promote(String(window.appVersion || '0')));
            } else {
                promote(String(window.appVersion || '0'));
            }
        };

        const onMessage = (e) => {
            // Same-window post from our own Flutter host only. targetOrigin '*' on the sender does
            // not widen e.origin, so both checks are meaningful, and the version must look like one.
            if (e.source !== window || e.origin !== window.location.origin) return;
            const v = e.data?.appVersion;
            if (typeof v !== 'string' && typeof v !== 'number') return;
            if (!/^[0-9]{1,3}(\.[0-9]{1,3}){0,3}\/*$/.test(String(v))) return;
            promote(String(v));
        };

        window.addEventListener('flutterInAppWebViewPlatformReady', onReady);
        window.addEventListener('message', onMessage);
        // the bridge may have arrived between the render-phase check and this effect
        if (window.flutter_inappwebview) onReady();

        return () => {
            window.removeEventListener('flutterInAppWebViewPlatformReady', onReady);
            window.removeEventListener('message', onMessage);
        };
    }, [isApp]);

    useEffect(() => {
        // ReactPixel.init(FB_PIXELID, advancedMatching, options);
        setUserContext(prev => ({ ...prev, homePath: '/' }));

        if (os.isIos || os.isAndroid) {
            document.body.classList.add(classes.mobile);
        } else {
            document.body.classList.add(classes.desktop);
        }
    }, []);

    useEffect(() => {
        authAPI.setupUserContext(userState, cookieState);
    }, []);

    useEffect(() => {
        if (!sendGuestToLanding) return;
        // A university page sends its visitor to the school's sign-in instead (#30); every other app is
        // unchanged.
        const university = location.pathname === `/${appRoots.university}` || location.pathname.startsWith(`/${appRoots.university}/`);
        window.location.replace(university
            ? universityGuestDestination(location.pathname, location.search)
            : `${explorerPaths.landing}?redirect=${encodeURIComponent(location.pathname + location.search + location.hash)}`);
    }, [sendGuestToLanding, location.pathname]);

    return userContext.user && userContext.user.role && !holdForLanding ? (
        <>
            <GlobalStyling />
            <UserContext.Provider value={userState}>
                <FeatureFlagContext.Provider value={featureFlags}>
                    <UrlParamContext.Provider value={urlParams}>
                        <KeyboardContext.Provider value={keyboardState}>
                                { testSite && (
                                    <div style={{ position: 'fixed', top: 0, right: 0, zIndex: 99999, padding: '5px 8px', background: 'rgba(255,0,0,.2)', fontSize: '12px', color: '#b45656',
                                    }}>{testSite[1]} site, {isApp ? `app v${window.versionParam}` : 'web'}
                                    </div>
                                )}
                            <Suspense fallback={LoadingSpinner}>
                                {props.children}
                            </Suspense>
                            { userContext.loginAs && <LoginAsBanner user={userContext.user} loginAs={userContext.loginAs} /> }
                        </KeyboardContext.Provider>
                    </UrlParamContext.Provider>
                </FeatureFlagContext.Provider>
            </UserContext.Provider>
        </>
    ) : null;
};

// remove console.log on product mode
// if (process.env.NODE_ENV === 'production' && !window.location.hostname.match(/^alpha\./)) {
//     console.log = () => {};
// }

export const withBase = (Component) => {
    const inner = (props) => (
        <Base>
            <Component {...props} />
        </Base>
    )

    return inner;
}

export default Base;
